import { radius } from '@alison/ui';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { bandOf, readable, type ScoreBand } from './ScoreVisuals.js';
import type { CreativeHalves } from './scoredBatch.js';

const PANEL_SX = {
  gap: 2,
  px: 4,
  py: 4,
  minWidth: 260,
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 2,
  border: '1px solid',
  textAlign: 'center',
};

const CHIP_SX = {
  px: 2,
  py: 0.5,
  borderRadius: `${radius.pill}px`,
  fontSize: 13,
  fontWeight: 600,
};

const VERDICT: Record<ScoreBand, string> = {
  low: 'Rework before launch',
  medium: 'Apply the top recommendations',
  high: 'Ready to launch',
};

const HALF_SX = {
  flex: 1,
  gap: 0.5,
  alignItems: 'center',
  justifyContent: 'space-between',
  textAlign: 'center',
};

export function CreativeScorePanel({ halves }: { halves: CreativeHalves }): ReactNode {
  const theme = useTheme();
  const band = bandOf(halves.score);
  const tone = theme.palette.score[band];
  const shown = [
    { label: 'PERFORMANCE SCORE', ...halves.performance },
    { label: 'BEST PRACTICE SCORE', ...halves.bestPractice },
  ].filter((half) => half.score !== null);

  return (
    <Stack sx={{ ...PANEL_SX, borderColor: tone.main, bgcolor: alpha(tone.main, 0.08) }}>
      <Typography sx={{ fontWeight: 600, color: tone.text }}>OVERALL SCORE</Typography>
      <Stack sx={{ gap: 1.5, alignItems: 'center' }}>
        <Stack sx={{ alignItems: 'center' }}>
          <Typography sx={{ fontSize: 56, fontWeight: 500 }}>{readable(halves.score)}</Typography>
          <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>Out of 10</Typography>
        </Stack>
        <Typography sx={{ ...CHIP_SX, color: tone.text, bgcolor: alpha(tone.text, 0.1) }}>
          {VERDICT[band]}
        </Typography>
      </Stack>

      {shown.length === 0 ? null : (
        <>
          <Divider flexItem sx={{ borderColor: tone.main }} />
          <Stack direction="row" sx={{ gap: 3, alignSelf: 'stretch' }}>
            {shown.map((half) => (
              <Stack key={half.label} sx={HALF_SX}>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {half.label} · {half.weight}%
                </Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 500 }}>
                  {readable(half.score)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
}
